import { Add, Delete } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { Language, WorkflowState } from "../../../interfaces/doc";
import FormTextField from "../../Common/FormTextField";
import { defaultWorflowState } from "./ConfigDefault";
import { useFormConfigContext } from "./ConfigFormContext";

const ConfigGeneralite: React.FC = () => {
  const { control, setValue, watch } = useFormConfigContext();
  const {
    fields,
    append: addState,
    remove: removeState,
  } = useFieldArray({
    control,
    name: `config.workflow`,
  });

  const states = watch(`config.workflow`);

  const handleCheckboxChange = (
    index: number,
    field: keyof Omit<WorkflowState, "name">,
    checked: boolean
  ) => {
    if (!states) return;
    else
      setValue(
        "config.workflow",
        fields.map((f, i) =>
          checked
            ? i === index
              ? {
                  ...states[i],
                  is_default: false,
                  set_on_validation_success: false,
                  set_on_validation_failure: false,
                  set_on_validation_error: false,
                  [field]: true,
                }
              : {
                  ...states[i],
                  [field]: false,
                }
            : i === index
              ? {
                  ...states[i],
                  [field]: false,
                }
              : states[i]
        )
      );
  };

  return (
    <>
      <Typography className="text-xl font-semibold">Généralités</Typography>
      <Stack rowGap={2} className="px-4 py-4 overflow-y-scroll">
        <FormTextField
          size="small"
          control={control}
          name="name"
          label="Nom de la configuration"
        />
        <Controller
          render={({ field: { onChange, ...props } }) => (
            <Autocomplete
              options={Object.values(Language)}
              getOptionLabel={(lang) => {
                if (lang === Language.FRANCAIS) return "Français";
                else if (lang === Language.ENGLISH) return "English";
                else return "Deutsh";
              }}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Langage" />
              )}
              onChange={(e, lang) => onChange(lang)}
              {...props}
            />
          )}
          name="config.language"
          control={control}
        />

        <div className="flex items-center justify-between">
          <FormLabel className="font-semibold">Statuts</FormLabel>

          <Button
            color="info"
            size="small"
            onClick={() => addState(defaultWorflowState())}
          >
            <Add />
          </Button>
        </div>
        <Stack spacing={2}>
          {fields.map((field, index) => (
            <Card key={field.id} variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Controller
                      name={`config.workflow.${index}.name`}
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          fullWidth
                          label="Statut"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error?.message}
                          size="small"
                        />
                      )}
                    />
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => removeState(index)}
                      sx={{ ml: 1 }}
                    >
                      <Delete />
                    </IconButton>
                  </Stack>

                  <Stack direction="row" flexWrap="wrap" gap={2}>
                    <Controller
                      name={`config.workflow.${index}.is_default`}
                      control={control}
                      render={({ field: { value } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={value}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  index,
                                  "is_default",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Statut par défaut"
                        />
                      )}
                    />

                    <Controller
                      name={`config.workflow.${index}.set_on_validation_success`}
                      control={control}
                      render={({ field: { value } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={value}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  index,
                                  "set_on_validation_success",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Statut dossier validé"
                        />
                      )}
                    />

                    <Controller
                      name={`config.workflow.${index}.set_on_validation_failure`}
                      control={control}
                      render={({ field: { value } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={value}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  index,
                                  "set_on_validation_failure",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Statut dossier non validé"
                        />
                      )}
                    />

                    <Controller
                      name={`config.workflow.${index}.set_on_validation_error`}
                      control={control}
                      render={({ field: { value } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={value}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  index,
                                  "set_on_validation_error",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Statut dossier invalidé"
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Stack>
    </>
  );
};
export default ConfigGeneralite;
